import { useContext } from 'react'
import { useMutation, useQueryClient } from 'react-query'
import { apiPost, apiPatch } from 'api'
import AuthContext from 'context/auth-context'
import UserContext from 'context/user-context'

// Single mutation hook for the monitoring review workflow. Handles both
// create (first time a user reviews a transaction) and update (changing the
// status of an existing monitoring review).
//
// Invoke `mutate({ transactionId, reviewStatusId, reason, reviewId })`. When
// `reviewId` is present we PATCH; otherwise we POST a new review scoped to
// the transaction itself as the source record.
//
// Pass `duplicateAnalyticId` to decide every side of a duplicate (Monitoring::DuplicateDecision).
export default function useMonitoringReview({ options = {} } = {}) {
  const token = useContext(AuthContext)
  const { municipality_id } = useContext(UserContext)
  const queryClient = useQueryClient()

  return useMutation(
    async ({ transactionId, reviewStatusId, reason, reviewId, duplicateAnalyticId }) => {
      if (duplicateAnalyticId) {
        return apiPost({
          url: `municipalities/${municipality_id}/monitoring/duplicate_review`,
          token,
          objectName: 'duplicate_decision',
          params: {
            transaction_id: transactionId,
            analytic_id: duplicateAnalyticId,
            review_status_id: reviewStatusId,
            initial_comment: reason || null
          }
        })
      }

      if (reviewId) {
        return apiPatch({
          url: `reviews/${reviewId}`,
          token,
          objectName: 'review',
          params: {
            review_status_id: reviewStatusId,
            initial_comment: reason || null
          }
        })
      }

      return apiPost({
        url: `transactions/${transactionId}/reviews`,
        token,
        objectName: 'review',
        params: {
          review_status_id: reviewStatusId,
          initial_comment: reason || null,
          reviewable_type: 'Transaction',
          reviewable_id: transactionId,
          source_record_type: 'Transaction',
          source_record_id: transactionId
        }
      })
    },
    {
      ...options,
      onSuccess: (data, variables, context) => {
        queryClient.invalidateQueries(['analytics-monitoring-transactions'])
        queryClient.invalidateQueries(['analytics-monitoring'])
        queryClient.invalidateQueries(['monitoring-high-risk-transactions'])
        // The workflow index counts its backlog from the same reviews.
        queryClient.invalidateQueries(['monitoring-workflows'])
        if (options.onSuccess) options.onSuccess(data, variables, context)
      }
    }
  )
}
